import { latestSnapshotWriter } from "../profile/latestSnapshotWriter";
import { queuePreferenceList, projectPendingPreferences } from "../preferences/preferenceOutbox";
import { normalizeSectionOptions, type SectionOptionListId } from "../settings/sectionOptions";
import { planCall } from "../billing/planService";
import { uploadPlanFile, deletePlanFile } from "../billing/planService";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  verifyBeforeUpdateEmail,
  type User
} from "firebase/auth";
import { Bytes, collection, deleteDoc, deleteField, doc, getDoc, getDocFromCache, getDocs, getDocsFromServer, onSnapshot, serverTimestamp, setDoc, updateDoc, runTransaction, writeBatch, type Unsubscribe } from "firebase/firestore";
import { deleteObject, getBytes, getDownloadURL, getMetadata, ref, uploadString } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions, storage } from "../firebase/client";
import { deletePersonalDocumentBlob, uploadPersonalDocumentBlob } from "../data/personalDocumentStorage";
import { defaultPreferences } from "../config/constants";
import type { PersonalDocument, PersonalDocumentType, SupportedLanguage, TravelCityExperience, TravelCountryExperience, TravelWorld, UserProfile } from "../types/domain";

export type EditableProfileFields = Pick<UserProfile,
  "firstName" | "lastName" | "phone" | "phonePrefix" | "birthDate" | "gender" |
  "nationality" | "country" | "province" | "city" | "address" | "jobType" | "appUseReason" | "bio" | "socialLinks" | "showSocialLinks"
>;

export async function loadProfile(uid: string): Promise<UserProfile | null> {
  const snapshot = await getDoc(doc(db, "users", uid));
  return snapshot.exists() ? profileWithLocalWorld(uid, snapshot.data() as UserProfile) : null;
}

export async function loadCachedProfile(uid: string): Promise<UserProfile | null> {
  try {
    const snapshot = await getDocFromCache(doc(db, "users", uid));
    return snapshot.exists() ? profileWithLocalWorld(uid, snapshot.data() as UserProfile) : null;
  } catch {
    return null;
  }
}

function profileWithLocalWorld(uid: string, profile: UserProfile): UserProfile {
  if (profile.preferences) profile = { ...profile, preferences: projectPendingPreferences(uid, profile.preferences) };
  const remoteWorld = profile.preferences?.travelWorldSync || profile.travelWorld;
  const localWorld = getTravelWorldSnapshot(uid, remoteWorld, profile.visitedCountries);
  return localWorld.countries.length || localWorld.cities.length || localWorld.updatedAt
    ? { ...profile, travelWorld: localWorld }
    : profile;
}

export function watchProfile(uid:string, publish:(profile:UserProfile|null, metadata:{fromCache:boolean;hasPendingWrites:boolean})=>void):Unsubscribe {
  return onSnapshot(doc(db,"users",uid),{includeMetadataChanges:true},snapshot=>{
    if(!snapshot.exists()&&snapshot.metadata.fromCache)return;
    publish(snapshot.exists()?profileWithLocalWorld(uid,snapshot.data() as UserProfile):null,{fromCache:snapshot.metadata.fromCache,hasPendingWrites:snapshot.metadata.hasPendingWrites});
  },()=>undefined);
}

export async function completeSocialProfile(input: {
  user: User;
  firstName: string;
  lastName: string;
  username: string;
}): Promise<UserProfile> {
  const callable = httpsCallable<
    { firstName: string; lastName: string; username: string },
    { profile: UserProfile }
  >(functions, "claimUsernameAndProfile");
  const result = await callable({
    firstName: input.firstName,
    lastName: input.lastName,
    username: input.username
  });
  return result.data.profile;
}

export async function updateProfileFields(uid: string, patch: Partial<EditableProfileFields>): Promise<void> {
  const sanitized: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(patch)) {
    sanitized[key] = typeof raw === "string" ? raw.trim() : raw;
  }
  await updateDoc(doc(db, "users", uid), { ...sanitized, updatedAt: serverTimestamp() });
}

const TRAVEL_WORLD_LOCAL_PREFIX = "tripai.travelWorld.";
const TRAVEL_WORLD_PENDING_PREFIX = "tripai.travelWorld.pending.";
const TRAVEL_WORLD_EVENT = "tripai:travel-world-updated";

function travelWorldLocalKey(uid: string): string {
  return `${TRAVEL_WORLD_LOCAL_PREFIX}${uid}`;
}

function travelWorldPendingKey(uid: string): string {
  return `${TRAVEL_WORLD_PENDING_PREFIX}${uid}`;
}

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function hasPendingLocalTravelWorld(uid: string): boolean {
  if (!canUseLocalStorage()) return false;
  try {
    return window.localStorage.getItem(travelWorldPendingKey(uid)) === "1";
  } catch {
    return false;
  }
}

function normalizeTravelWorld(input?: Partial<TravelWorld> | null, legacyCountries?: string[]): TravelWorld {
  const sourceCountries: TravelCountryExperience[] = Array.isArray(input?.countries)
    ? input!.countries as TravelCountryExperience[]
    : Array.isArray(legacyCountries)
      ? legacyCountries
          .filter((countryCode) => /^[A-Za-z]{2}$/.test(String(countryCode || "")))
          .map((countryCode): TravelCountryExperience => ({ countryCode, status: "visited" }))
      : [];
  const sourceCities: TravelCityExperience[] = Array.isArray(input?.cities) ? input!.cities as TravelCityExperience[] : [];
  const countries = new Map<string, TravelCountryExperience>();
  const cities = new Map<string, TravelCityExperience>();

  for (const raw of sourceCountries) {
    const countryCode = String(raw?.countryCode || "").trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(countryCode)) continue;
    const item: TravelCountryExperience = {
      countryCode,
      status: raw.status === "lived" ? "lived" : "visited"
    };
    const feedback = String(raw.feedback || "").trim();
    const privateNote = String(raw.privateNote || feedback).trim();
    const publicReview = String(raw.publicReview || "").trim();
    if (feedback) item.feedback = feedback;
    if (privateNote) item.privateNote = privateNote;
    if (publicReview) item.publicReview = publicReview;
    if (Number.isFinite(Number(raw.rating)) && Number(raw.rating) >= 1 && Number(raw.rating) <= 5) item.rating = Number(raw.rating);
    if (typeof raw.reviewPublic === "boolean") item.reviewPublic = raw.reviewPublic;
    if (raw.updatedAt) item.updatedAt = String(raw.updatedAt);
    countries.set(countryCode, item);
  }

  for (const raw of sourceCities) {
    const name = String(raw?.name || "").trim();
    const countryCode = String(raw?.countryCode || "").trim().toUpperCase();
    if (!name || !/^[A-Z]{2}$/.test(countryCode)) continue;
    const id = String(raw.id || "").trim() || `city_${countryCode}_${name.toLocaleLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
    const item: TravelCityExperience = {
      id,
      name,
      countryCode,
      status: raw.status === "lived" ? "lived" : "visited"
    };
    const feedback = String(raw.feedback || "").trim();
    const privateNote = String(raw.privateNote || feedback).trim();
    const publicReview = String(raw.publicReview || "").trim();
    if (feedback) item.feedback = feedback;
    if (privateNote) item.privateNote = privateNote;
    if (publicReview) item.publicReview = publicReview;
    if (Number.isFinite(Number(raw.rating)) && Number(raw.rating) >= 1 && Number(raw.rating) <= 5) item.rating = Number(raw.rating);
    if (typeof raw.reviewPublic === "boolean") item.reviewPublic = raw.reviewPublic;
    if (raw.updatedAt) item.updatedAt = String(raw.updatedAt);
    cities.set(id, item);
  }

  const world: TravelWorld = { countries: Array.from(countries.values()), cities: Array.from(cities.values()) };
  if (input?.updatedAt) world.updatedAt = String(input.updatedAt);
  return world;
}

function readLocalTravelWorld(uid: string): TravelWorld | undefined {
  if (!canUseLocalStorage()) return undefined;
  try {
    const raw = window.localStorage.getItem(travelWorldLocalKey(uid));
    if (!raw) return undefined;
    return normalizeTravelWorld(JSON.parse(raw) as Partial<TravelWorld>);
  } catch {
    return undefined;
  }
}

function travelWorldTimestamp(world?: TravelWorld): number {
  const timestamp = world?.updatedAt ? Date.parse(world.updatedAt) : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function itemTimestamp(updatedAt: string | undefined, fallback: number): number {
  const parsed = updatedAt ? Date.parse(updatedAt) : fallback;
  return Number.isFinite(parsed) ? parsed : fallback;
}

function mergeTravelWorlds(...sources: Array<TravelWorld | undefined>): TravelWorld {
  const countries = new Map<string, { item: TravelCountryExperience; timestamp: number }>();
  const cities = new Map<string, { item: TravelCityExperience; timestamp: number }>();
  let latestWorldTimestamp = 0;
  let latestWorldUpdatedAt: string | undefined;

  for (const source of sources) {
    if (!source) continue;
    const normalized = normalizeTravelWorld(source);
    const worldTimestamp = travelWorldTimestamp(normalized);
    if (worldTimestamp >= latestWorldTimestamp) {
      latestWorldTimestamp = worldTimestamp;
      latestWorldUpdatedAt = normalized.updatedAt;
    }
    for (const item of normalized.countries) {
      const timestamp = itemTimestamp(item.updatedAt, worldTimestamp);
      const current = countries.get(item.countryCode);
      if (!current || timestamp > current.timestamp || (timestamp === current.timestamp && item.status === "lived" && current.item.status !== "lived")) {
        countries.set(item.countryCode, { item, timestamp });
      }
    }
    for (const item of normalized.cities) {
      const timestamp = itemTimestamp(item.updatedAt, worldTimestamp);
      const current = cities.get(item.id);
      if (!current || timestamp >= current.timestamp) cities.set(item.id, { item, timestamp });
    }
  }

  const merged: TravelWorld = {
    countries: Array.from(countries.values()).map(({ item }) => item),
    cities: Array.from(cities.values()).map(({ item }) => item)
  };
  if (latestWorldUpdatedAt) merged.updatedAt = latestWorldUpdatedAt;
  return merged;
}

function travelWorldSignature(world: TravelWorld | undefined): string {
  if (!world) return "";
  const normalized = normalizeTravelWorld(world);
  const countries = normalized.countries
    .map((item) => `${item.countryCode}:${item.status}:${item.feedback || ""}:${item.updatedAt || ""}`)
    .sort();
  const cities = normalized.cities
    .map((item) => `${item.id}:${item.name}:${item.countryCode}:${item.status}:${item.feedback || ""}:${item.updatedAt || ""}`)
    .sort();
  return JSON.stringify({ countries, cities });
}

function writeLocalTravelWorld(uid: string, world: TravelWorld, pending: boolean): void {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(travelWorldLocalKey(uid), JSON.stringify(world));
    if (pending) window.localStorage.setItem(travelWorldPendingKey(uid), "1");
    else window.localStorage.removeItem(travelWorldPendingKey(uid));
  } catch {
    // Browser storage is a resilience layer; Firebase attempts still continue.
  }
}

function emitTravelWorld(uid: string, world: TravelWorld): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(TRAVEL_WORLD_EVENT, { detail: { uid, world } }));
}

function travelWorldStoragePath(uid: string): string {
  return `users/${uid}/world/travel-world.json`;
}

async function loadTravelWorldFromStorage(uid: string): Promise<TravelWorld | undefined> {
  try {
    const bytes = await getBytes(ref(storage, travelWorldStoragePath(uid)));
    const raw = new TextDecoder().decode(bytes);
    return normalizeTravelWorld(JSON.parse(raw) as Partial<TravelWorld>);
  } catch {
    return undefined;
  }
}

type WorldCloudResult={firestoreSaved:boolean;storageSaved:boolean};
const travelWorldWriters=new Map<string,(value:TravelWorld)=>Promise<WorldCloudResult>>();
function syncTravelWorldCloud(uid:string,travelWorld:TravelWorld):Promise<WorldCloudResult>{
 let writer=travelWorldWriters.get(uid);
 if(!writer){writer=latestSnapshotWriter<TravelWorld,WorldCloudResult>(value=>persistTravelWorldCloud(uid,value),value=>JSON.stringify(value));travelWorldWriters.set(uid,writer)}
 const local=readLocalTravelWorld(uid);
 const value=local&&hasPendingLocalTravelWorld(uid)&&travelWorldTimestamp(local)>=travelWorldTimestamp(travelWorld)?local:travelWorld;
 return writer(value);
}

async function persistTravelWorldCloud(uid: string, travelWorld: TravelWorld): Promise<{ firestoreSaved: boolean; storageSaved: boolean }> {
  const visitedCountries = travelWorld.countries.map((item) => item.countryCode);
  const livedCountries = travelWorld.countries.filter((item) => item.status === "lived").map((item) => item.countryCode);
  let firestoreSaved = false;
  let storageSaved = false;

  try {
    await updateDoc(doc(db, "users", uid), {
      "preferences.travelWorldSync": travelWorld,
      updatedAt: serverTimestamp()
    });
    firestoreSaved = true;
  } catch {
    // Preferences are the authoritative cross-device channel. Storage is the fallback below.
  }

  // Compatibility mirror for older clients. It is intentionally separate so restrictive
  // rules on legacy root fields cannot invalidate the authoritative preferences write.
  try {
    await setDoc(doc(db, "users", uid), {
      travelWorld,
      visitedCountries,
      livedCountries,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch {
    // Older Firestore rules may reject these fields; preferences.travelWorldSync remains valid.
  }

  try {
    await uploadString(ref(storage, travelWorldStoragePath(uid)), JSON.stringify(travelWorld), "raw", {
      contentType: "application/json",
      customMetadata: { purpose: "travel-world" }
    });
    storageSaved = true;
  } catch {
    // Storage can be unavailable offline; keep local cache and retry later.
  }

  if ((firestoreSaved || storageSaved) && JSON.stringify(readLocalTravelWorld(uid)) === JSON.stringify(travelWorld)) writeLocalTravelWorld(uid, travelWorld, false);
  return { firestoreSaved, storageSaved };
}

export function getTravelWorldSnapshot(uid: string, remoteWorld?: TravelWorld, legacyCountries?: string[]): TravelWorld {
  const legacyWorld = legacyCountries?.length ? normalizeTravelWorld(undefined, legacyCountries) : undefined;
  return mergeTravelWorlds(remoteWorld ? normalizeTravelWorld(remoteWorld) : undefined, readLocalTravelWorld(uid), legacyWorld);
}

export async function loadTravelWorld(uid: string, remoteWorld?: TravelWorld, legacyCountries?: string[]): Promise<TravelWorld> {
  let normalizedRemote = remoteWorld ? normalizeTravelWorld(remoteWorld) : undefined;
  let resolvedLegacy = legacyCountries;

  if (!normalizedRemote) {
    try {
      const snapshot = await getDoc(doc(db, "users", uid));
      if (snapshot.exists()) {
        const data = snapshot.data() as Partial<UserProfile>;
        normalizedRemote = normalizeTravelWorld(data.preferences?.travelWorldSync || data.travelWorld, data.visitedCountries);
        resolvedLegacy = Array.isArray(data.visitedCountries) ? data.visitedCountries : resolvedLegacy;
      }
    } catch {
      // Offline: local/Storage sources below still provide the best available state.
    }
  }

  const legacyWorld = resolvedLegacy?.length ? normalizeTravelWorld(undefined, resolvedLegacy) : undefined;
  const stored = await loadTravelWorldFromStorage(uid);
  const latestLocal=readLocalTravelWorld(uid);
  const merged = hasPendingLocalTravelWorld(uid)&&latestLocal ? latestLocal : mergeTravelWorlds(normalizedRemote, stored, latestLocal, legacyWorld);
  const pending = hasPendingLocalTravelWorld(uid);

  writeLocalTravelWorld(uid, merged, pending);
  emitTravelWorld(uid, merged);

  const cloudSignature = travelWorldSignature(normalizedRemote);
  const storageSignature = travelWorldSignature(stored);
  const mergedSignature = travelWorldSignature(merged);
  if (pending || mergedSignature !== cloudSignature || mergedSignature !== storageSignature) {
    await syncTravelWorldCloud(uid, merged);
  }
  return merged;
}

export function watchTravelWorld(
  uid: string,
  onChange: (world: TravelWorld | undefined, visitedCountries: string[] | undefined) => void
): Unsubscribe {
  let latestRemote: TravelWorld | undefined;
  let latestLegacy: string[] | undefined;
  let disposed = false;

  const notify = () => {
    if (disposed) return;
    const localWorld = readLocalTravelWorld(uid);
    const legacyWorld = latestLegacy?.length ? normalizeTravelWorld(undefined, latestLegacy) : undefined;
    const merged = mergeTravelWorlds(latestRemote, localWorld, legacyWorld);
    writeLocalTravelWorld(uid, merged, hasPendingLocalTravelWorld(uid));
    onChange(merged, latestLegacy);
  };

  const unsubscribeFirestore = onSnapshot(doc(db, "users", uid), (snapshot) => {
    if (!snapshot.exists()) {
      latestRemote = undefined;
      latestLegacy = undefined;
      notify();
      return;
    }
    const data = snapshot.data() as Partial<UserProfile>;
    latestRemote = normalizeTravelWorld(data.preferences?.travelWorldSync || data.travelWorld, data.visitedCountries);
    latestLegacy = Array.isArray(data.visitedCountries) ? data.visitedCountries : undefined;
    const localWorld = readLocalTravelWorld(uid);
    const merged = mergeTravelWorlds(latestRemote, localWorld);
    writeLocalTravelWorld(uid, merged, hasPendingLocalTravelWorld(uid));
    onChange(merged, latestLegacy);
    if (travelWorldSignature(merged) !== travelWorldSignature(latestRemote) || hasPendingLocalTravelWorld(uid)) {
      void syncTravelWorldCloud(uid, merged).catch(() => undefined);
    }
  }, () => notify());

  const onWorldEvent = (event: Event) => {
    const detail = (event as CustomEvent<{ uid?: string }>).detail;
    if (detail?.uid === uid) notify();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key === travelWorldLocalKey(uid)) notify();
  };
  const onRefresh = () => {
    void loadTravelWorld(uid, latestRemote, latestLegacy).then(() => notify()).catch(() => undefined);
  };
  let syncInterval: number | undefined;

  if (typeof window !== "undefined") {
    window.addEventListener(TRAVEL_WORLD_EVENT, onWorldEvent as EventListener);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onRefresh);
    syncInterval = window.setInterval(onRefresh, 8000);
  }

  void loadTravelWorld(uid).then(() => notify()).catch(() => undefined);

  return () => {
    disposed = true;
    unsubscribeFirestore();
    if (typeof window !== "undefined") {
      window.removeEventListener(TRAVEL_WORLD_EVENT, onWorldEvent as EventListener);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onRefresh);
      if (typeof syncInterval === "number") window.clearInterval(syncInterval);
    }
  };
}

export async function updateTravelWorld(uid: string, input: TravelWorld): Promise<void> {
  const now = new Date().toISOString();
  const travelWorld = normalizeTravelWorld({ ...input, updatedAt: now });

  // Immediate local update for UI responsiveness, then cloud sync for browser/phone consistency.
  writeLocalTravelWorld(uid, travelWorld, true);
  emitTravelWorld(uid, travelWorld);
  await syncTravelWorldCloud(uid, travelWorld);
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("tripai:gamification-source-updated", { detail: { uid } }));
}


function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefinedDeep(item)) as T;
  }
  if (value && typeof value === "object" && !(value instanceof Date)) {
    const clean: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (child === undefined) continue;
      clean[key] = stripUndefinedDeep(child);
    }
    return clean as T;
  }
  return value;
}

export async function updatePreferences(
  uid: string,
  patch: Partial<UserProfile["preferences"]>,
  options?: { sectionOptionsBase?: UserProfile["preferences"]["sectionOptions"]; resetLists?: SectionOptionListId[] }
): Promise<void> {
  if(patch.sectionOptions){
    const cached = options?.sectionOptionsBase ? null : await loadCachedProfile(uid);
    for(const [listId,next] of Object.entries(patch.sectionOptions)){
      const id=listId as SectionOptionListId;
      const base=options?.sectionOptionsBase?.[id] || normalizeSectionOptions(cached?.preferences,id);
      await queuePreferenceList(uid,id,{base,next,reset:options?.resetLists?.includes(id)||false});
    }
  }
  // Write only the fields explicitly changed. Never replay a stale profile snapshot.
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(stripUndefinedDeep(patch))) {
    if(key === "sectionOptions")continue;
    if(key === "sectionOptionRevisions")throw new Error("INVALID_PREFERENCE_FIELD");
    if (key === "notifications") {
      for (const [child, entry] of Object.entries(value as Record<string, unknown>)) {
        if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(child)) throw new Error("INVALID_PREFERENCE_FIELD");
        fields[`preferences.${key}.${child}`] = entry;
      }
    } else {
      if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(key)) throw new Error("INVALID_PREFERENCE_FIELD");
      fields[`preferences.${key}`] = value;
    }
  }
  if (!Object.keys(fields).length) return;
  await updateDoc(doc(db, "users", uid), { ...fields, updatedAt: serverTimestamp() });
}

const PERSONAL_DOCUMENT_CHUNK_BYTES = 360_000;
const PERSONAL_DOCUMENT_RETENTION_DAYS = 30;

function mapPersonalDocumentSnapshot(entry: { id: string; data: () => Record<string, unknown> }): PersonalDocument {
  const data = entry.data() as Partial<PersonalDocument>;
  const cloudBackend = data.cloudBackend === "firestore" ? "firestore" : data.cloudBackend === "storage" ? "storage" : undefined;
  const cloudFileId = typeof data.cloudFileId === "string" && data.cloudFileId ? data.cloudFileId : undefined;
  const url = typeof data.url === "string" && data.url ? data.url : undefined;
  const uploaded = data.status === "uploaded" && Boolean(url || (cloudBackend === "firestore" && cloudFileId));
  return {
    id: entry.id,
    type: data.type === "passport" || data.type === "id" || data.type === "drivingLicense" || data.type === "taxCode" ? data.type : "other",
    name: String(data.name || "document"),
    mimeType: String(data.mimeType || "application/octet-stream"),
    size: Number(data.size || 0),
    queueId: String(data.queueId || ""),
    storagePath: String(data.storagePath || ""),
    url,
    cloudBackend,
    cloudFileId,
    status: uploaded ? "uploaded" : "pending",
    deletedAt: typeof data.deletedAt === "string" && data.deletedAt ? data.deletedAt : undefined,
    purgeAfter: typeof data.purgeAfter === "string" && data.purgeAfter ? data.purgeAfter : undefined,
    createdAt: String(data.createdAt || new Date().toISOString()),
    updatedAt: String(data.updatedAt || data.createdAt || new Date().toISOString())
  };
}

async function listPersonalDocumentsRaw(uid: string): Promise<PersonalDocument[]> {
  const snapshot = await getDocs(collection(db, "users", uid, "documents"));
  return snapshot.docs
    .map((entry) => mapPersonalDocumentSnapshot(entry as unknown as { id: string; data: () => Record<string, unknown> }))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function listPersonalDocuments(uid: string): Promise<PersonalDocument[]> {
  const items = await listPersonalDocumentsRaw(uid);
  return items.filter((item) => !item.deletedAt);
}

export async function listDeletedPersonalDocuments(uid: string): Promise<PersonalDocument[]> {
  const items = await listPersonalDocumentsRaw(uid);
  return items.filter((item) => Boolean(item.deletedAt));
}

export async function savePersonalDocument(uid: string, input: PersonalDocument): Promise<void> {
  // Firestore rejects `undefined`. Firebase Storage uploads legitimately have no
  // `cloudFileId` (that field is used only for the Firestore-chunk backend).
  // Do not modify the global Firestore SDK settings or silently remove nulls.
  const payload = Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined));
  await setDoc(doc(db, "users", uid, "documents", input.id), payload, { merge: true });
}

function safeDocumentStorageName(name: string): string {
  return String(name || "document").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 120) || "document";
}

async function deleteFirestoreDocumentFile(uid: string, documentId: string): Promise<void> {
  // Current layout: chunks live under the document itself so rules that already
  // authorize users/{uid}/documents/** also cover the binary payload.
  const nestedChunks = await getDocs(collection(db, "users", uid, "documents", documentId, "fileChunks")).catch(() => null);
  if (nestedChunks) {
    const nestedBatch = writeBatch(db);
    nestedChunks.docs.forEach((entry) => nestedBatch.delete(entry.ref));
    if (!nestedChunks.empty) await nestedBatch.commit();
  }

  // Legacy layout used by package 22. Keep cleanup support for documents that
  // may already have been uploaded with that schema.
  const legacyChunks = await getDocs(collection(db, "users", uid, "documentFiles", documentId, "chunks")).catch(() => null);
  if (legacyChunks) {
    const legacyBatch = writeBatch(db);
    legacyChunks.docs.forEach((entry) => legacyBatch.delete(entry.ref));
    legacyBatch.delete(doc(db, "users", uid, "documentFiles", documentId));
    await legacyBatch.commit().catch(() => undefined);
  }
}



function toBlobArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

export async function loadPersonalDocumentCloudFile(uid: string, documentId: string): Promise<Blob | null> {
  // Current nested layout.
  const nested = await getDocs(collection(db, "users", uid, "documents", documentId, "fileChunks")).catch(() => null);
  if (nested && !nested.empty) {
    const ordered = nested.docs
      .map((entry) => ({ id: entry.id, data: entry.data() as { bytes?: Bytes; index?: number; mimeType?: string } }))
      .sort((a, b) => Number(a.data.index ?? a.id) - Number(b.data.index ?? b.id));
    const parts: BlobPart[] = ordered
      .map((entry) => entry.data.bytes instanceof Bytes ? entry.data.bytes.toUint8Array() : new Uint8Array())
      .filter((part) => part.length > 0)
      .map((part) => toBlobArrayBuffer(part));
    if (parts.length) {
      return new Blob(parts, { type: String(ordered[0]?.data.mimeType || "application/octet-stream") });
    }
  }

  // Package-22 inline fallback compatibility.
  const metadata = await getDoc(doc(db, "users", uid, "documents", documentId));
  if (metadata.exists()) {
    const data = metadata.data() as { fileBytes?: Bytes; fileMimeType?: string; mimeType?: string };
    if (data.fileBytes instanceof Bytes) {
      return new Blob([toBlobArrayBuffer(data.fileBytes.toUint8Array())], { type: String(data.fileMimeType || data.mimeType || "application/octet-stream") });
    }
  }

  // Package-22 sibling documentFiles compatibility.
  const manifest = await getDoc(doc(db, "users", uid, "documentFiles", documentId)).catch(() => null);
  if (!manifest?.exists()) return null;
  const meta = manifest.data() as { mimeType?: string; size?: number };
  const chunks = await getDocs(collection(db, "users", uid, "documentFiles", documentId, "chunks"));
  const ordered = chunks.docs
    .map((entry) => ({ id: entry.id, data: entry.data() as { bytes?: Bytes; index?: number } }))
    .sort((a, b) => Number(a.data.index ?? a.id) - Number(b.data.index ?? b.id));
  const parts: BlobPart[] = ordered
    .map((entry) => entry.data.bytes instanceof Bytes ? entry.data.bytes.toUint8Array() : new Uint8Array())
    .filter((part) => part.length > 0)
    .map((part) => toBlobArrayBuffer(part));
  if (!parts.length) return null;
  return new Blob(parts, { type: String(meta.mimeType || "application/octet-stream") });
}

export async function uploadPersonalDocumentFile(uid: string, documentId: string, blob: Blob, originalName: string, mimeType: string): Promise<{ url?: string; storagePath: string; cloudBackend: "storage" | "firestore"; cloudFileId?: string }> {
  const path = `users/${uid}/documents/${documentId}/${originalName.replace(/[^a-zA-Z0-9._-]/g, "_") || "document"}`;
  const normalized = blob.type ? blob : new Blob([blob], { type: mimeType });
  try {
    const uploaded = await uploadPlanFile(normalized, path);
    if (!uploaded.url) throw new Error("document/storage-url-missing");
    return { url: uploaded.url, storagePath: path, cloudBackend: "storage" };
  } catch (storageError) {
    // An access/plan/quota denial is authoritative; do not bypass the plan.
    const storageCode = String((storageError as {code?:string})?.code || "");
    if (/permission-denied|unauthenticated|resource-exhausted|quota|limit|failed-precondition/.test(storageCode)) throw storageError;

    // Direct Firebase Storage uses an authenticated user session and tries the
    // actual configured bucket and the two legacy document paths. It validates
    // a readable remote object before reporting an upload as successful.
    let directError: unknown = null;
    try {
      const direct = await uploadPersonalDocumentBlob(uid, documentId, normalized, originalName, mimeType);
      return {url:direct.url, storagePath:direct.storagePath,cloudBackend:"storage"};
    } catch (error) { directError = error; }

    // Last compatible fallback: authorized Firestore document chunks.
    // Never report success unless the cloud bytes can be read back from the server.
    // Firestore's 1-MiB document limit requires small, individually verifiable chunks.
    const maxBytes = 6_000_000;
    if (normalized.size > maxBytes) throw storageError;
    const bytes = new Uint8Array(await normalized.arrayBuffer());
    if (!bytes.length) throw new Error("document/empty-file");
    const chunkSize = 180_000;
    const chunks: Array<{index:number; bytes:Bytes}> = [];
    for (let index = 0, pos = 0; pos < bytes.length; index++, pos += chunkSize) {
      chunks.push({index,bytes:Bytes.fromUint8Array(bytes.slice(pos,pos+chunkSize))});
    }
    const created: Array<ReturnType<typeof doc>> = [];
    try {
      for (const chunk of chunks) {
        const reference=doc(db,"users",uid,"documents",documentId,"fileChunks",String(chunk.index).padStart(4,"0"));
        await setDoc(reference,{ index:chunk.index, bytes:chunk.bytes, mimeType:normalized.type||mimeType },{merge:false});
        created.push(reference);
      }
      const remote = await getDocsFromServer(collection(db,"users",uid,"documents",documentId,"fileChunks"));
      const stored = remote.docs
        .map(entry => entry.data() as {index?:number;bytes?:Bytes})
        .sort((a,b) => Number(a.index) - Number(b.index));
      const complete = stored.length===chunks.length && stored.every((entry,index) => {
        if (entry.index!==index || !(entry.bytes instanceof Bytes)) return false;
        const savedBytes=entry.bytes.toUint8Array();
        const expectedBytes=chunks[index].bytes.toUint8Array();
        return savedBytes.length===expectedBytes.length && savedBytes.every((value,offset)=>value===expectedBytes[offset]);
      });
      if (!complete) throw new Error("document/cloud-verify-failed");
      return { storagePath:"", cloudBackend:"firestore", cloudFileId:documentId };
    } catch (fallbackError) {
      await Promise.allSettled(created.map(ref => deleteDoc(ref)));
      const errors = [storageError, directError, fallbackError].map((cause) =>
        String((cause as {code?:string})?.code || (cause as Error)?.message || "unavailable").slice(0, 100));
      const failure = new Error(`document/cloud-upload-failed: ${errors.join(" | ")}`);
      (failure as Error & {code:string}).code = "document/cloud-upload-failed";
      throw failure;
    }
  }
}

export async function markPersonalDocumentUploaded(uid: string, documentId: string, url: string): Promise<void> {
  await setDoc(doc(db, "users", uid, "documents", documentId), {
    url,
    cloudBackend: "storage",
    status: "uploaded",
    updatedAt: new Date().toISOString()
  }, { merge: true });
}

export async function renamePersonalDocument(uid: string, documentId: string, name: string): Promise<void> {
  await setDoc(doc(db, "users", uid, "documents", documentId), {
    name: name.trim(),
    updatedAt: new Date().toISOString()
  }, { merge: true });
}

export async function changePersonalDocumentType(uid: string, documentId: string, type: PersonalDocumentType): Promise<void> {
  await setDoc(doc(db, "users", uid, "documents", documentId), {
    type,
    updatedAt: new Date().toISOString()
  }, { merge: true });
}

export async function softDeletePersonalDocument(uid: string, documentId: string): Promise<{ purgeAfter: string }> {
  const deletedAt = new Date();
  const purgeAfter = new Date(deletedAt.getTime() + PERSONAL_DOCUMENT_RETENTION_DAYS * 24 * 60 * 60 * 1000).toISOString();
  await setDoc(doc(db, "users", uid, "documents", documentId), {
    deletedAt: deletedAt.toISOString(),
    purgeAfter,
    updatedAt: deletedAt.toISOString()
  }, { merge: true });
  return { purgeAfter };
}

export async function restorePersonalDocument(uid: string, documentId: string): Promise<void> {
  if ((await planCall()).plan !== "premium") throw new Error("MUNDELY_COMPLETE_REQUIRED");
  await setDoc(doc(db, "users", uid, "documents", documentId), {
    deletedAt: deleteField(),
    purgeAfter: deleteField(),
    updatedAt: new Date().toISOString()
  }, { merge: true });
}

export async function deletePersonalDocument(uid: string, documentId: string, storagePath?: string): Promise<void> {
  if (storagePath?.startsWith("firestore")) {
    await httpsCallable(functions, "deleteMundelyLegacyDocument")({ documentId });
  } else if (storagePath) {
    await deletePlanFile(storagePath);
  }
  await deleteDoc(doc(db, "users", uid, "documents", documentId));
}

export async function purgeExpiredPersonalDocuments(uid: string): Promise<number> {
  const deleted = await listDeletedPersonalDocuments(uid);
  const now = Date.now();
  const expired = deleted.filter((item) => item.purgeAfter && Date.parse(item.purgeAfter) <= now);
  for (const item of expired) {
    await deletePersonalDocument(uid, item.id, item.storagePath);
  }
  return expired.length;
}

export async function uploadProfilePhoto(uid: string, file: File): Promise<string> {
  const photoUrl = (await uploadPlanFile(file, `users/${uid}/profile/avatar.jpg`)).url;
  await setDoc(doc(db, "users", uid), {
    photoUrl,
    updatedAt: serverTimestamp()
  }, { merge: true });
  return photoUrl;
}


export async function uploadProfileGalleryPhoto(uid: string, file: File): Promise<string[]> {
  const current = await loadProfile(uid);
  if (!current) throw new Error("PROFILE_NOT_FOUND");
  const photos = Array.isArray(current.personalPhotos) ? current.personalPhotos.filter(Boolean) : [];
  

  const extension = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
  const id = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `photo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const url = (await uploadPlanFile(file, `users/${uid}/profile/gallery/${id}.${extension}`)).url;
  const next = [...photos, url];
  await setDoc(doc(db, "users", uid), {
    personalPhotos: next,
    updatedAt: serverTimestamp()
  }, { merge: true });
  return next;
}

export async function removeProfileGalleryPhoto(uid: string, url: string): Promise<string[]> {
  const current = await loadProfile(uid);
  if (!current) throw new Error("PROFILE_NOT_FOUND");
  const photos = Array.isArray(current.personalPhotos) ? current.personalPhotos.filter(Boolean) : [];
  const next = photos.filter((item) => item !== url);
  try {
    await deletePlanFile(ref(storage, url).fullPath);
  } catch (error) {
    const code = String((error as { code?: string } | null)?.code || "");
    if (code && code !== "storage/object-not-found" && code !== "storage/invalid-url") throw error;
  }
  await setDoc(doc(db, "users", uid), {
    personalPhotos: next,
    updatedAt: serverTimestamp()
  }, { merge: true });
  return next;
}

export async function changeUsername(username: string): Promise<UserProfile> {
  const callable = httpsCallable<{ username: string }, { profile: UserProfile }>(functions, "changeUsername");
  return (await callable({ username: username.trim() })).data.profile;
}

export async function requestEmailChange(newEmail: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("AUTH_REQUIRED");
  await verifyBeforeUpdateEmail(user, newEmail.trim().toLowerCase(), {
    url: window.location.origin,
    handleCodeInApp: false
  });
}

export async function changePassword(input: {
  currentPassword?: string;
  newPassword: string;
}): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("AUTH_REQUIRED");
  const hasPassword = user.providerData.some((item) => item.providerId === "password");
  if (hasPassword && input.currentPassword && user.email) {
    await reauthenticateWithCredential(
      user,
      EmailAuthProvider.credential(user.email, input.currentPassword)
    );
  }
  await updatePassword(user, input.newPassword);
}

export async function syncProfileEmailFromAuth(): Promise<string> {
  const callable = httpsCallable<Record<string, never>, { email: string }>(functions, "syncAuthEmail");
  return (await callable({})).data.email;
}

export async function requestAccountDeletion(): Promise<{ deleteAfter: string }> {
  const callable = httpsCallable<Record<string, never>, { deleteAfter: string }>(functions, "requestAccountDeletion");
  return (await callable({})).data;
}

export async function cancelAccountDeletion(): Promise<void> {
  const callable = httpsCallable<Record<string, never>, { restored: boolean }>(functions, "cancelAccountDeletion");
  await callable({});
}

export async function requestDataExport(): Promise<{ status: string; requestId: string }> {
  const callable = httpsCallable<Record<string, never>, { status: string; requestId: string }>(functions, "requestDataExport");
  return (await callable({})).data;
}

export async function saveOnboarding(input: {
  uid: string;
  language: SupportedLanguage;
  currency: string;
  measurementSystem: "metric" | "imperial";
  dateFormat: "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD";
  weekStartsOn?: "monday" | "sunday";
  notifications: UserProfile["preferences"]["notifications"];
  analyticsEnabled: boolean;
  photoUrl?: string;
}): Promise<void> {
  const userRef = doc(db, "users", input.uid);
  // Onboarding may be retried after a delayed read. Existing choices always win.
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(userRef);
    if (!snapshot.exists()) throw new Error("PROFILE_NOT_FOUND");
    const current = snapshot.data() as UserProfile;
    if (current.onboardingCompletedAt) return;
    const preferences = {
      ...defaultPreferences(input.language),
      ...current.preferences,
      language: input.language,
      currency: input.currency,
      measurementSystem: input.measurementSystem,
      dateFormat: input.dateFormat,
      weekStartsOn: input.weekStartsOn || "monday",
      notifications: { ...current.preferences?.notifications, ...input.notifications },
      analyticsEnabled: input.analyticsEnabled
    };
    transaction.update(userRef, {
      preferences: stripUndefinedDeep(preferences),
      ...(input.photoUrl && !current.photoUrl ? { photoUrl: input.photoUrl } : {}),
      onboardingCompletedAt: new Date().toISOString(),
      updatedAt: serverTimestamp()
    });
  });
}
